import type { ReactNode } from 'react';

import { AccessDenied } from '@/pages/settings/AccessDenied';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions, useHasPermission, type ModuleRequirement } from '@/lib/permissions';

interface RequirePermissionProps {
  /** A module, or a list of which any one will do. */
  module: ModuleRequirement;
  action?: string;
  children: ReactNode;
}

/**
 * Route-level permission gate.
 *
 * The navigation hides links a user cannot use, but until now nothing stopped a
 * deep link: `routes.tsx` checks authentication only, so pasting a URL rendered
 * the page and left each one to defend itself. The backend still refuses the
 * mutations, so this is not the security boundary — but a screen that draws for
 * someone who may not use it is a bug, and it matters more now that there is a
 * persona whose whole value is a narrowed world.
 *
 * Waits for permissions to load rather than flashing a denial at someone who
 * does have access.
 */
export function RequirePermission({ module, action = 'view', children }: RequirePermissionProps) {
  const { loading } = usePermissions();
  const allowed = useHasPermission(module, action);

  if (loading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!allowed) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
